/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { useEffect, useMemo, useState } from 'react';

import { Locale, LocaleKeys } from '../../../core/locale';
import {
    CursorManager,
    KeyManager,
    LocationUrls,
    ScreenControllerTypes,
    ScreenTaskControllerTypes,
} from '../../../core/navigation';
import { TScreen } from '../../../core/navigation/types';
import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { ListTypes } from '../../../core/utils';
import { setListEntryFocus } from '../../../store/list/list.actions';
import { setManageOptionsList } from '../../../store/settings/manage/manage.reducers';
import { List, Mousetrap, ScreenPreview } from '../../common';
import { useScreenController } from '../../hooks';
import { SettingsTemplate } from '../SettingsTemplate';

import '../SettingsCommon.scss';

const TAG = 'ManageScreen';

const Manage = (): JSX.Element => {
    const dispatch = useTivoDispatch();
    const [isCursorActive, setisCursorActive] = useState(false);
    const { optionsList, selectedOption } = useTivoSelector((state: RootState) => state.manage);

    useScreenController<TScreen>({
        identifier: TAG,
        properties: {
            url: LocationUrls.MANAGE_SCREEN,
            screenController: ScreenControllerTypes.MANAGE_SCREEN,
            taskController: ScreenTaskControllerTypes.NO_TASK,
        },
    });

    const onCursorStateChangeCallback = (state) => {
        setisCursorActive(state);
        if (!isCursorActive) dispatch(setListEntryFocus(ListTypes.MANAGE, true));
    };

    const selectedOptionIndex = useMemo(() => {
        return optionsList.reduce(
            (acc, option, index) => (option.title === selectedOption?.title ? index : acc),
            0,
        );
    }, [optionsList, selectedOption]);

    useEffect(() => {
        dispatch(setManageOptionsList());
        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);

        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }

        return () => {
            CursorManager.unsubscribe(refCursorManager);
        };
    }, []);

    const renderManageList = () => {
        return (
            <div className="settings-common-content multi-value-item">
                <List
                    type={ListTypes.MANAGE}
                    itemList={optionsList}
                    initialFocusedIndex={selectedOptionIndex}
                    initialSelectedIndex={selectedOptionIndex}
                    generateItemView={(item: any, itemClass, index) => (
                        <Mousetrap
                            key={index}
                            value={`${index}_${ListTypes.MANAGE}`}
                            className={`item ${itemClass}`}
                        >
                            <span>{item.title}</span>
                            {item.value && <span className="list-value">[{item.value}]</span>}
                        </Mousetrap>
                    )}
                />
            </div>
        );
    };

    return (
        <SettingsTemplate title={Locale.getString(LocaleKeys.SETTINGS_MANAGE_TITLE)}>
            {renderManageList()}
            <ScreenPreview>
                <div>{selectedOption?.preview}</div>
            </ScreenPreview>
        </SettingsTemplate>
    );
};

export default Manage;
